import { useEffect, useRef, type ReactNode } from 'react'
import type { Battle } from '@mezha/api'
import { useWorldQuery } from '../../../api/endpoints/world'
import { useSlotRef } from '../../../shell/ShellContext'
import { atlasFor } from './model'
import { atlasURL, drawScene } from './renderer'
import type { Playback } from './usePlayback'
import { resolveCombatArt } from './atlas'
import { useSceneImages } from './useSceneImages'

export function BattleScene({ battle, play, reduced, target, skip, openLog, children }: {
  battle: Battle; play: Playback | null; reduced: boolean; target: string | null; skip: () => void; openLog: () => void; children: ReactNode
}) {
  const chatRef = useSlotRef('combat-chat')
  const twilight = battle.twilight_round > 0 && battle.round >= battle.twilight_round
  const canvas = useRef<HTMLCanvasElement>(null)
  const latest = useRef({ battle, play, reduced, target })
  latest.current = { battle, play, reduced, target }
  const { data: world } = useWorldQuery()
  const meadow = battle.kind === 'pve' && battle.actors.some(a => atlasFor(a) === 'lugar')
  const background = meadow ? '/assets/combat/meadow-stage-v01.webp' : world?.place?.scene || world?.cell_scene || '/assets/ui/backdrop-wide.webp'
  const urls = [...battle.actors, ...(play?.before ?? [])].map(a => resolveCombatArt(a)?.url ?? (atlasFor(a) ? atlasURL(atlasFor(a)!) : '')).filter(Boolean)
  const { images, failed, retry } = useSceneImages(urls)
  useEffect(() => {
    const el = canvas.current
    const c = el?.getContext('2d')
    if (!el || !c) return
    let frame = 0, last = 0, w = 0, h = 0
    const resize = () => {
      const rect = el.getBoundingClientRect()
      w = rect.width; h = rect.height
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      el.width = Math.round(w * dpr); el.height = Math.round(h * dpr)
      c.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    const observer = new ResizeObserver(resize)
    observer.observe(el); resize()
    const draw = (now: number) => {
      const s = latest.current
      if (!document.hidden && now - last >= (s.play ? 16 : s.reduced ? 200 : 40)) {
        last = now; drawScene(c, w, h, s.battle, s.play, now, s.reduced, images.current, s.target)
      }
      frame = requestAnimationFrame(draw)
    }
    frame = requestAnimationFrame(draw)
    return () => { cancelAnimationFrame(frame); observer.disconnect() }
  }, [images])
  return <section className={`combat-scene${twilight ? ' is-twilight' : ''}${reduced ? ' is-reduced' : ''}`} aria-label="Сцена боя" data-playing-round={play?.round.round ?? ''}>
    <img className="combat-scene__backdrop" src={background} alt="" onError={e => { e.currentTarget.style.visibility = 'hidden' }} />
    <div className="combat-scene__mist" aria-hidden="true" />
    <div className="combat-scene__bar">
      <span>{play ? `Размен ${play.round.round} · ходы одновременны` : battle.my_choice ? 'Ход принят · ждём размена' : 'Выберите удар и защиту'}</span>
      {play && <button type="button" className="linkbtn" onClick={skip}>Пропустить</button>}
      <button type="button" className="linkbtn" onClick={openLog}>Журнал боя</button>
      <div className="combat-scene__chat" ref={chatRef} />
    </div>
    {children}
    <canvas ref={canvas} aria-hidden="true" />
    {failed && <span className="combat-scene__error">Часть рисунков недоступна. Бой и журнал работают. <button type="button" className="linkbtn" onClick={retry}>Повторить рисунки</button></span>}
  </section>
}
